import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useMemo } from 'react';
import type { CalendarMonthDto } from 'api/generated';
import { SimpleIconButton } from 'components/common/buttons/simple-icon-button';
import { TixIconButton } from 'components/common/buttons/tix-icon-button';
import { EventsCalendarNavigationSelector } from 'components/common/selectors/events-calendar-navigation-selector';
import { useAppState } from 'lib/app-state';
import { getMonthName } from 'lib/helpers/calendar-helpers';
import type { useCalendar } from 'lib/hooks/use-calendar';
export const CalendarNavigation = ({
  calendar,
  year,
  setYear,
  availableMonths,
  simpleView
}: {
  calendar: ReturnType<typeof useCalendar>;
  year?: number;
  setYear?: (year: number) => void;
  availableMonths: CalendarMonthDto[];
  simpleView?: boolean;
}) => {
  const appState = useAppState();
  const monthOptions = useMemo(() => [...Array(12).keys()].map(i => ({
    value: i,
    label: getMonthName(appState.locale, i)
  })), [appState.locale]);

  // Months are compared as (year, month) keys — never as Date instants,
  // whose timezone-dependent getTime() values make the bounds unreliable.
  const monthKey = (year: number, month: number) => year * 100 + month;
  const viewingKey = monthKey(calendar.viewing.getFullYear(), calendar.viewing.getMonth() + 1);
  const availableKeys = availableMonths.map(m => monthKey(m.year!, m.month!));
  const prevDisabled = availableKeys.length === 0 || viewingKey <= Math.min(...availableKeys);
  const nextDisabled = availableKeys.length === 0 || viewingKey >= Math.max(...availableKeys);
  const prevAvailableMonth = () => {
    const target = availableMonths.filter(m => monthKey(m.year!, m.month!) < viewingKey).sort((a, b) => monthKey(b.year!, b.month!) - monthKey(a.year!, a.month!))[0];
    if (target) calendar.setViewing(new Date(target.year!, target.month! - 1));
  };
  const nextAvailableMonth = () => {
    const target = availableMonths.filter(m => monthKey(m.year!, m.month!) > viewingKey).sort((a, b) => monthKey(a.year!, a.month!) - monthKey(b.year!, b.month!))[0];
    if (target) calendar.setViewing(new Date(target.year!, target.month! - 1));
  };
  if (simpleView) {
    return <Stack direction="row" sx={{
      gap: 1,
      width: '100%',
      justifyContent: 'space-between'
    }}>
        <SimpleIconButton ariaLabel="Previous" icon={<ChevronLeft />} onClick={prevAvailableMonth} disabled={prevDisabled} />
        <Typography variant="h6" sx={{
        alignSelf: 'center'
      }}>
          {/* viewing is a locally constructed month-start date, so its local
              getters identify the month exactly without formatting through a
              timezone */}
          {`${getMonthName(appState.locale, calendar.viewing.getMonth(), 'long')} ${calendar.viewing.getFullYear()}`}
        </Typography>
        <SimpleIconButton ariaLabel="Next" icon={<ChevronRight />} onClick={nextAvailableMonth} disabled={nextDisabled} />
      </Stack>;
  }
  return <Box sx={{
    display: 'flex',
    gap: 1,
    width: {
      xs: '100%',
      sm: 'auto'
    },
    justifyContent: {
      xs: 'space-between',
      sm: 'flex-start'
    }
  }}>
      <TixIconButton variant={'outlined'} color="monochrome" onClick={prevAvailableMonth} disabled={prevDisabled}>
        <ChevronLeft />
      </TixIconButton>
      <EventsCalendarNavigationSelector calendar={calendar} year={year ? year : calendar.viewing.getFullYear()} setYear={setYear ? setYear : () => void 0} monthOptions={monthOptions} availableMonths={availableMonths} />
      <TixIconButton variant="outlined" color="monochrome" onClick={nextAvailableMonth} disabled={nextDisabled}>
        <ChevronRight />
      </TixIconButton>
    </Box>;
};